import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { useQueryClient } from "@tanstack/react-query";
import React, { useCallback, useEffect, useState, memo } from "react";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "../../../Context/AppContext";
import { useMakePayment } from "../../../api/mutations/useMakePayment";
import { useGetBilling } from "../../../api/queries/useGetBilling";
import { useGetInvestorDetails } from "../../../api/queries/MuslimInvest/useGetInvestorDetails";
import { toast } from "react-toastify";
import { useSaveInvestor } from "../../../api/mutations/MuslimInvest/useSaveInvestor";
import {
  logPaymentError,
  getPaymentErrorSource,
  getPaymentErrorMessage,
} from "../../../utils/paymentErrorLogger";

const PRICE = "500.00";

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: "16px",
      color: "#000",
      "::placeholder": { color: "#999" },
    },
    invalid: { color: "red" },
  },
};

const CARD_EXPIRY_CVC_OPTIONS = {
  style: {
    base: {
      fontSize: "16px",
      color: "#000",
      "::placeholder": { color: "#999" },
    },
  },
};

const InvestorCheckout = memo(function InvestorCheckout() {
  const { objectId, user } = useAppContext();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const stripe = useStripe();
  const elements = useElements();
  const [billingAddress, setBillingAddress] = useState("");
  const [useSavedMethod, setUseSavedMethod] = useState(false);
  const navigate = useNavigate();
  const { mutate } = useMakePayment();
  const queryClient = useQueryClient();
  const { data: investorData } = useGetInvestorDetails({
    userId: user?.user.id.toString() || "",
  });

  const profile = investorData ? investorData[0] : null;

  const { mutate: saveInvestorDetailsMutation } = useSaveInvestor({
    onSuccess: () => {
      // toast.success("Investor details saved successfully");
      queryClient.invalidateQueries({ queryKey: ["investor-data"] });
      navigate("/investing", { replace: true });
    },
    onError: () => {
      toast.error("Unable to process please try again later");
    },
  });

  const { data: paymentMethods } = useGetBilling({ userId: objectId || "" });

  useEffect(() => {
    if ((paymentMethods?.length ?? 0) > 0) {
      setUseSavedMethod(true);
    }
  }, [paymentMethods]);

  const saveProfileInvestorDetails = useCallback(() => {
    if (!profile) {
      toast.error("Investor profile not found!");
      setLoading(false);
      return;
    }

    setLoading(true);
    const formData = new FormData();

    Object.entries(profile).forEach(([key, value]) => {
      if (key === "id" && typeof value === "number" && value <= 0) return;
      if (value !== undefined && value !== null)
        formData.append(key, String(value));
    });

    formData.set("status", "Registered");
    saveInvestorDetailsMutation(formData);
  }, [profile, saveInvestorDetailsMutation]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      const msg = "Stripe.js has not loaded yet.";
      logPaymentError({
        flow: "investor-checkout",
        errorSource: "frontend",
        message: msg,
      });
      setError(msg);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const cardNumberElement = elements?.getElement(CardNumberElement);

      if (!stripe || !elements) {
        const msg = "Stripe has not loaded.";
        logPaymentError({
          flow: "investor-checkout",
          errorSource: "frontend",
          message: msg,
        });
        setError(msg);
        setLoading(false);
        return;
      }

      let newPaymentMethod;
      if (!useSavedMethod && cardNumberElement) {
        const { paymentMethod, error: pmError } =
          await stripe.createPaymentMethod({
            type: "card",
            card: cardNumberElement,
          });
        if (pmError || !paymentMethod) {
          const msg = pmError?.message || "Card creation failed.";
          logPaymentError({
            flow: "investor-checkout",
            errorSource: "stripe",
            message: msg,
            error: pmError,
          });
          setError(msg);
          setLoading(false);
          return;
        }
        newPaymentMethod = paymentMethod;
      }

      const selectedPaymentMethodId =
        useSavedMethod && paymentMethods?.[0]?.paymentMethodId
          ? paymentMethods[0].paymentMethodId
          : newPaymentMethod?.id;

      if (!selectedPaymentMethodId) {
        const msg = "No valid payment method found.";
        logPaymentError({
          flow: "investor-checkout",
          errorSource: "frontend",
          message: msg,
        });
        setError(msg);
        setLoading(false);
        return;
      }

      mutate(
        {
          paymentMethodTypes: ["card"],
          customerId: "",
          paymentMethodId: selectedPaymentMethodId,
          userId: objectId || "",
          name: `${user?.user?.firstName || ""} ${
            user?.user?.lastName || ""
          }`.trim(),
          email: user?.user?.email || "",
          productId: "prod_TSmcoQ1fzGoMLS",
          discountCode: "",
          isEvent: false,
        },
        {
          onSuccess: async (response) => {
            const clientSecret = response.clientSecret;
            if (!clientSecret) {
              const msg = "No client secret returned.";
              logPaymentError({
                flow: "investor-checkout",
                errorSource: "backend",
                message: msg,
                responseData: response,
              });
              setError(msg);
              setLoading(false);
              return;
            }

            const { paymentIntent, error: confirmError } =
              await stripe.confirmCardPayment(clientSecret, {
                payment_method: selectedPaymentMethodId,
              });

            if (confirmError) {
              const msg =
                confirmError.message || "Payment confirmation failed.";
              logPaymentError({
                flow: "investor-checkout",
                errorSource: "stripe",
                message: msg,
                error: confirmError,
              });
              setError(msg);
              setLoading(false);
              return;
            }
            if (paymentIntent && paymentIntent.status === "succeeded") {
              saveProfileInvestorDetails();
            }
            setLoading(false);
          },
          onError: (err: unknown) => {
            const source = getPaymentErrorSource(err);
            const msg = getPaymentErrorMessage(err);
            logPaymentError({
              flow: "investor-checkout",
              errorSource: source,
              message: msg,
              error: err,
              responseStatus:
                err && typeof err === "object" && "response" in err
                  ? (err as { response?: { status?: number } }).response?.status
                  : undefined,
              responseData:
                err && typeof err === "object" && "response" in err
                  ? (err as { response?: { data?: unknown } }).response?.data
                  : undefined,
            });
            setError(msg);
            setLoading(false);
          },
        }
      );
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : "Unexpected error occurred.";
      logPaymentError({
        flow: "investor-checkout",
        errorSource: "frontend",
        message: msg,
        error: err,
      });
      setError(msg);
      setLoading(false);
    }
    },
    [
      stripe,
      elements,
      useSavedMethod,
      paymentMethods,
      mutate,
      objectId,
      user,
      saveProfileInvestorDetails,
    ]
  );

  const handleUseSavedCard = useCallback(() => setUseSavedMethod(true), []);
  const handleUseNewCard = useCallback(() => setUseSavedMethod(false), []);
  const handleBillingAddressChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => setBillingAddress(e.target.value),
    []
  );

  return (
    <div className="m-4 rounded-4 p-4 shadow border-0">
      <div className="row">
        {/* LEFT SIDE — PAYMENT FORM */}
        <div className="col d-flex flex-column">
          <h3 className="fw-bold mb-3">Investor Registration Checkout</h3>

          <div className="alert alert-info p-3">
            <p className="mb-2">
              Thank you for confirming your acceptance of our Terms and
              Conditions. A copy has been emailed to your registered email
              address.
            </p>
            <p className="mb-2">
              You are now on the final step of completing your investor profile
              and gaining access to live pitches.
            </p>
            <p className="fw-bold mb-0 text-danger">
              Please note this payment is non-refundable.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="w-100" autoComplete="off">
            <div className="d-flex flex-column gap-3">
              <div className="border rounded-4 p-3 shadow-sm bg-light">
                <h5 className="fw-bold mb-3">Booking Summary</h5>

                <div className="d-flex justify-content-between mb-2">
                  <span>Investor Registration</span>
                  <span className="fw-bold">£{PRICE}</span>
                </div>

                <hr />

                <div className="d-flex justify-content-between">
                  <span>Total</span>
                  <span className="fw-bold fs-5">£{PRICE}</span>
                </div>
              </div>

              {paymentMethods && paymentMethods?.length > 0 ? (
                <div className="mb-3">
                  <label className="form-label">Choose Payment Method</label>

                  <div className="form-check mb-2">
                    <input
                      type="radio"
                      className="form-check-input"
                      id="savedCard"
                      checked={useSavedMethod}
                      onChange={handleUseSavedCard}
                    />
                    <label className="form-check-label" htmlFor="savedCard">
                      Use saved card (**** **** **** {paymentMethods?.[0].last4}
                      , {paymentMethods[0].brand})
                    </label>
                  </div>

                  <div className="form-check">
                    <input
                      type="radio"
                      className="form-check-input"
                      id="newCard"
                      checked={!useSavedMethod}
                      onChange={handleUseNewCard}
                    />
                    <label className="form-check-label" htmlFor="newCard">
                      Use a different card
                    </label>
                  </div>
                </div>
              ) : null}

              {!useSavedMethod && (
                <div className="row g-2">
                  <div className="col-md-6 mb-3">
                    <label>Card Number</label>
                    <div className="form-control">
                      <CardNumberElement options={CARD_ELEMENT_OPTIONS} />
                    </div>
                  </div>

                  <div className="col-md-3 mb-3">
                    <label>Expiry</label>
                    <div className="form-control">
                      <CardExpiryElement options={CARD_EXPIRY_CVC_OPTIONS} />
                    </div>
                  </div>

                  <div className="col-md-3 mb-3">
                    <label>CVC</label>
                    <div className="form-control">
                      <CardCvcElement options={CARD_EXPIRY_CVC_OPTIONS} />
                    </div>
                  </div>

                  <div className="col-md-6 mb-3">
                    <label>Billing Address (optional)</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Billing Address"
                      value={billingAddress}
                      autoComplete="new-address"
                      onChange={handleBillingAddressChange}
                    />
                  </div>
                </div>
              )}

              {error && (
                <div className="alert alert-danger py-2 px-3" role="alert">
                  {error}
                </div>
              )}

              <div className="d-flex justify-content-between mt-4">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={!stripe || loading}
                >
                  {loading ? "Processing..." : "Pay Now"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
});

InvestorCheckout.displayName = "InvestorCheckout";

export default InvestorCheckout;
